import { PaymentMethods } from "./interfaces";

function isClarityReady(
  w: Window,
): w is Window & { clarity: NonNullable<Window["clarity"]> } {
  return typeof w.clarity === "function";
}

class DatalayerAPI {
  handleCurrentFormChange(parameters: {
    currentForm: number;
    stepName: string;
  }) {
    window.dataLayer.push({
      event: "flowStep",
      stepNumber: parameters.currentForm + 1,
      stepName: parameters.stepName,
    });
  }

  handleValidationErrors(formErrorsFullNames: string[]) {
    window.dataLayer.push({
      event: "validationError",
      errorMessages: formErrorsFullNames,
    });
  }

  handleOverlayValidationErrors(stepNumber: number, stepName: string) {
    window.dataLayer.push({
      event: "flowError",
      stepNumber,
      stepName,
      donationFormType: "donation overlay",
    });
  }

  handleSuccessfulPayment(parameters: {
    donationAmount: number;
    donationType: "monthly" | "one-time" | undefined;
    paymentId: string;
    isCoverFees: boolean;
    honourType: string;
    honourCardType: string;
    isCompanyGift: boolean;
    isOverlayForm: boolean;
    paymentMethod?: PaymentMethods;
  }) {
    window.dataLayer.push({
      event: "purchase",
      transactionId: parameters.paymentId,
      revenue: parameters.donationAmount.toFixed(2),
      donationFrequency: parameters.donationType,
      donorCoveringFees: parameters.isCoverFees ? "yes" : "no",
      donationType: parameters.honourType ? parameters.honourType : "standard",
      cardType: parameters.honourCardType,
      isCompanyGift: parameters.isCompanyGift ? "yes" : "no",
      paymentMethod: parameters.paymentMethod,
      ...(parameters.isOverlayForm && { donationFormType: "donation overlay" }),
    });
  }

  handleOverlayStepImpression(stepNumber: number, stepName: string) {
    window.dataLayer.push({
      event: "flowStep",
      stepNumber,
      stepName,
      donationFormType: "donation overlay",
    });
  }

  handleDonationTypeValuesChange(fieldName: string, fieldValue?: string) {
    window.dataLayer.push({
      event: "fieldChange",
      fieldName,
      ...(fieldValue ? { fieldValue } : {}),
    });
  }

  handleDonationAttemptStarted(sessionId: string) {
    window.dataLayer.push({
      event: "donationAttemptStarted",
      sessionId,
    });

    // Try sending to Clarity as custom identifiers
    this.identifyClarity(sessionId);
  }

  handleFieldValueChange(fieldName: string, fieldValue?: string) {
    window.dataLayer.push({
      event: "fieldChange",
      fieldName,
      ...(fieldValue ? { fieldValue } : {}),
    });
  }

  private identifyClarity(sessionId: string) {
    const tryIdentify = () => {
      if (!isClarityReady(window)) return false;

      window.clarity("identify", "anonymous", sessionId);
      return true;
    };

    if (tryIdentify()) return;

    // Retry briefly in case GTM loads Clarity after app mount
    const start = Date.now();
    const maxWaitMs = 5000;
    const intervalMs = 250;

    const timer = window.setInterval(() => {
      if (tryIdentify()) {
        window.clearInterval(timer);
        return;
      }
      if (Date.now() - start > maxWaitMs) {
        window.clearInterval(timer);
      }
    }, intervalMs);
  }
}

export default new DatalayerAPI();
